import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import { GRIDS } from './TgeReports'

export const isDigits = (v: string) => /^\d+$/.test(v.trim())

export type TgeForm = {
  grid: (typeof GRIDS)[number]
  custom: boolean
  customGridId: string
  customTableId: string
  configId: string
  issueKeysText: string
  dryRun: boolean
}

const labelClass = 'mb-1 block text-[11px] font-medium uppercase tracking-[0.06em] text-text-3'
const hintClass = 'mt-1 text-[11.5px] leading-snug text-text-3'

/** Migration parameters: grid, custom ids, config id, issue keys, dry run — one row of columns. */
export function TgeParams({ form, set, busy, keysUnparseable }: {
  form: TgeForm
  set: (patch: Partial<TgeForm>) => void
  busy: boolean
  keysUnparseable: boolean
}) {
  const { grid, custom, customGridId, customTableId, configId, issueKeysText, dryRun } = form
  return (
    <div className="grid shrink-0 grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1.2fr)_minmax(0,0.8fr)] gap-6 border-b border-line px-4 py-3">
      <div>
        <div id="tge-grid-label" className={labelClass}>Grid</div>
        <div role="radiogroup" aria-labelledby="tge-grid-label" className="space-y-1">
          {GRIDS.map((g) => (
            <label key={g.grid} className="flex cursor-pointer items-center gap-2 text-[12.5px]">
              <input
                type="radio"
                name="tge-grid"
                checked={!custom && grid.grid === g.grid}
                onChange={() => set({ grid: g, custom: false })}
                disabled={busy}
                className="accent-primary"
              />
              <span className={!custom && grid.grid === g.grid ? 'text-foreground' : 'text-text-2'}>{g.label}</span>
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
            <input
              type="radio"
              name="tge-grid"
              checked={custom}
              onChange={() => set({ custom: true })}
              disabled={busy}
              className="accent-primary"
            />
            <span className={custom ? 'text-foreground' : 'text-text-2'}>Custom grid</span>
          </label>
        </div>
        {custom && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="tge-custom-grid" className={labelClass}>TGE grid ID (DC)</label>
              <Input
                id="tge-custom-grid"
                inputMode="numeric"
                placeholder="e.g. 27214"
                value={customGridId}
                onChange={(e) => set({ customGridId: e.target.value })}
                disabled={busy}
                aria-invalid={(customGridId.trim() !== '' && !isDigits(customGridId)) || undefined}
                className="h-7 font-mono text-[12.5px]"
              />
            </div>
            <div>
              <label htmlFor="tge-custom-table" className={labelClass}>STAGIL table ID</label>
              <Input
                id="tge-custom-table"
                inputMode="numeric"
                placeholder="e.g. 4475"
                value={customTableId}
                onChange={(e) => set({ customTableId: e.target.value })}
                disabled={busy}
                aria-invalid={(customTableId.trim() !== '' && !isDigits(customTableId)) || undefined}
                className="h-7 font-mono text-[12.5px]"
              />
            </div>
            <p className={`col-span-2 ${hintClass}`}>
              Grid ID — the TGE custom field id in Jira DC. Table ID — the Id column in STAGIL Tables
              Configuration. Columns match by name; when the TGE ones carry internal ids (icost, icur…),
              run the audit and map them — the map is saved for this grid and table.
            </p>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="tge-config-id" className={labelClass}>{custom ? 'Config ID' : 'Config ID (optional)'}</label>
        <Input
          id="tge-config-id"
          inputMode="numeric"
          placeholder={custom ? 'required' : 'default 4253'}
          value={configId}
          onChange={(e) => set({ configId: e.target.value })}
          disabled={busy}
          // an empty required field is reported on submit, not flagged before typing
          aria-invalid={(configId.trim() !== '' && !isDigits(configId)) || undefined}
          className="h-7 font-mono text-[12.5px]"
        />
        <p className={hintClass}>
          STAGIL Field Configuration ID (Tables Configuration → Actions → Manage → Context details).
          {custom ? ' Required for a custom grid.' : ' Empty = known default (text/s: 4253).'}
        </p>
      </div>

      <div>
        <label htmlFor="tge-issue-keys" className={labelClass}>Issue keys (optional)</label>
        <Textarea
          id="tge-issue-keys"
          placeholder={'KSM-101, KSM-102\nKSM-103'}
          value={issueKeysText}
          onChange={(e) => set({ issueKeysText: e.target.value })}
          disabled={busy}
          aria-invalid={keysUnparseable || undefined}
          aria-describedby="tge-issue-keys-hint"
          className="min-h-16 font-mono text-[12.5px]"
        />
        {keysUnparseable
          ? (
            <p id="tge-issue-keys-hint" className="mt-1 text-[11.5px] text-danger">
              No issue keys found in this text. Enter keys, or clear the field to migrate the whole grid.
            </p>
          )
          : <p id="tge-issue-keys-hint" className={hintClass}>Comma- or newline-separated. Empty = migrate the whole grid.</p>}
      </div>

      <div>
        <div className={labelClass}>Mode</div>
        <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => set({ dryRun: e.target.checked })}
            disabled={busy}
            className="accent-primary"
          />
          <span className="text-foreground">Dry run</span>
        </label>
        <p className={hintClass}>
          {dryRun
            ? 'Transform + size checks, no writes to STAGIL.'
            : <span className="text-danger">Live: PUT-replaces each issue’s table in STAGIL.</span>}
        </p>
      </div>
    </div>
  )
}
